import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import { GridPager } from "../../common/pagination/GridPager";
import PayrollDataGrid, { payrollActionsCellClass, payrollActionsHeaderClass } from "./PayrollDataGrid";
import PayrollModal from "./PayrollModal";
import {
  createSalaryIncrement,
  fetchIncrementEmployeeGrid,
  fetchSalaryIncrementExportCsv,
  fetchSalaryIncrementExportExcel,
  fetchSalaryIncrementExportPdf,
  fetchSalaryIncrements,
  formatEffectiveDate,
  unwrapEmployeeGridList,
  formatIncrementMoney,
  IncrementStatus,
  formatSourceLabel,
  isEffectiveDateInPast,
  isEffectiveDateReached,
  salaryIncrementExportBasename,
  sourceBadgeClass,
  statusApiValue,
  statusBadgeClass,
  todayDateInputValue,
  toDateInputValue,
  unwrapOne,
  unwrapPaginatedList,
  updateSalaryIncrement,
  updateSalaryIncrementStatus,
} from "./salaryIncrementApi";
import { axiosBasic } from "../../../api/axios";
import { config } from "../../../api/endPoints";
import { triggerBlobDownload } from "../timesheets/timesheetApi";
import { Common } from "../../../utils";
import "../documents/Documents.css";
import "../timesheets/TimeSheets.css";
import "./Payroll.css";

const PAGE_SIZE = 20;
const REASON_MAX = 500;
const EMPLOYEE_SEARCH_DEBOUNCE_MS = 350;
const { Department, Office } = config.REST_API;

const FILTER = {
  ALL: "all",
};

const avatarColors = ["#6366f1", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];

function avatarColorFromName(name) {
  let hash = 0;
  const s = String(name ?? "");
  for (let i = 0; i < s.length; i++) hash = s.charCodeAt(i) + ((hash << 5) - hash);
  return avatarColors[Math.abs(hash) % avatarColors.length];
}

function employeeInitial(name) {
  const parts = String(name ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

const emptyForm = () => ({
  id: "",
  userId: "",
  newGrossSalary: "",
  effectiveFrom: todayDateInputValue(),
  reason: "",
});

const SalaryIncrementsPage = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [yearFilter, setYearFilter] = useState(FILTER.ALL);
  const [statusFilter, setStatusFilter] = useState(FILTER.ALL);
  const [departmentFilter, setDepartmentFilter] = useState(FILTER.ALL);
  const [officeFilter, setOfficeFilter] = useState(FILTER.ALL);
  const [exportLoading, setExportLoading] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [offices, setOffices] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState(/** @type {"create" | "edit"} */ ("create"));
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState("");
  const [debouncedEmployeeSearch, setDebouncedEmployeeSearch] = useState("");
  const [employeeSuggestions, setEmployeeSuggestions] = useState([]);
  const [employeeSearchLoading, setEmployeeSearchLoading] = useState(false);
  const [employeePickerOpen, setEmployeePickerOpen] = useState(false);
  const employeePickerRef = useRef(null);
  const [editScheduleMeta, setEditScheduleMeta] = useState(null);

  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [activateConfirm, setActivateConfirm] = useState(null);
  const [activateLoading, setActivateLoading] = useState(false);

  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    const years = [];
    for (let y = current + 1; y >= current - 5; y -= 1) years.push(y);
    return years;
  }, []);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(searchQuery.trim()), 350);
    return () => window.clearTimeout(id);
  }, [searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, yearFilter, statusFilter, departmentFilter, officeFilter]);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedEmployeeSearch(employeeSearchQuery.trim()), EMPLOYEE_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [employeeSearchQuery]);

  const listQuery = useMemo(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      search: debouncedSearch,
      year: yearFilter,
      status: statusFilter,
      departmentId: departmentFilter,
      officeId: officeFilter,
    }),
    [page, debouncedSearch, yearFilter, statusFilter, departmentFilter, officeFilter]
  );

  const exportQuery = useMemo(
    () => ({
      search: debouncedSearch,
      year: yearFilter,
      status: statusFilter,
      departmentId: departmentFilter,
      officeId: officeFilter,
    }),
    [debouncedSearch, yearFilter, statusFilter, departmentFilter, officeFilter]
  );

  const loadGrid = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetchSalaryIncrements(listQuery);
      const { items, totalCount: tc, message } = unwrapPaginatedList(res);
      if (message) {
        setRows([]);
        setTotalCount(0);
        setLoadError(message);
      } else {
        setRows(items);
        setTotalCount(tc);
      }
    } catch (e) {
      setRows([]);
      setTotalCount(0);
      setLoadError(e?.response?.data?.message || e?.message || t("app.payroll.increments.loadError"));
    } finally {
      setLoading(false);
    }
  }, [listQuery, t]);

  useEffect(() => {
    loadGrid();
  }, [loadGrid]);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [depRes, offRes] = await Promise.all([axiosBasic.get(Department.Get), axiosBasic.get(Office.GetCompanyOffices)]);
        const depBody = depRes.data || {};
        if (depBody.success && Array.isArray(depBody.data)) {
          setDepartments(
            depBody.data
              .map((d) => ({ id: d.id, name: d.name }))
              .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: "base" }))
          );
        }
        const offBody = offRes.data || {};
        if (offBody.success && Array.isArray(offBody.data)) {
          setOffices(
            offBody.data
              .map((o) => ({
                id: o.id,
                name: o.countryName ? `${o.name} (${o.countryName})` : o.name,
              }))
              .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: "base" }))
          );
        }
      } catch {
        setDepartments([]);
        setOffices([]);
      }
    };
    loadLookups();
  }, []);

  useEffect(() => {
    if (!modalOpen || !form.effectiveFrom || isEffectiveDateInPast(form.effectiveFrom)) {
      setEmployeeSuggestions([]);
      setEditScheduleMeta(null);
      return undefined;
    }

    const shouldFetchCreatePicker = modalMode === "create" && !selectedEmployee && employeePickerOpen;
    const shouldFetchEdit = modalMode === "edit" && form.userId;
    const shouldRefreshSelected =
      modalMode === "create" && selectedEmployee?.userId && !employeePickerOpen;

    if (!shouldFetchCreatePicker && !shouldFetchEdit && !shouldRefreshSelected) return undefined;

    const editEmployeeName =
      modalMode === "edit" && form.id ? rows.find((r) => r.id === form.id)?.employeeName : undefined;

    let cancelled = false;
    const run = async () => {
      setEmployeeSearchLoading(true);
      try {
        const res = await fetchIncrementEmployeeGrid({
          effectiveFrom: form.effectiveFrom,
          search:
            modalMode === "create"
              ? shouldRefreshSelected
                ? selectedEmployee.employeeName
                : debouncedEmployeeSearch
              : editEmployeeName,
          excludeIncrementId: modalMode === "edit" ? form.id : undefined,
        });
        const { items, message } = unwrapEmployeeGridList(res);
        if (cancelled) return;
        if (message) {
          setEmployeeSuggestions([]);
          if (modalMode === "edit") setEditScheduleMeta(null);
          return;
        }
        const valid = items.filter((e) => e.userId && e.employeeName);
        if (modalMode === "edit") {
          const match = valid.find((e) => e.userId === form.userId) ?? null;
          setEditScheduleMeta(match);
        } else if (shouldRefreshSelected) {
          const match = valid.find((e) => e.userId === selectedEmployee.userId);
          if (match) setSelectedEmployee(match);
        } else {
          setEmployeeSuggestions(valid);
        }
      } catch {
        if (!cancelled) {
          setEmployeeSuggestions([]);
          if (modalMode === "edit") setEditScheduleMeta(null);
        }
      } finally {
        if (!cancelled) setEmployeeSearchLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [
    modalOpen,
    modalMode,
    form.effectiveFrom,
    form.id,
    form.userId,
    employeePickerOpen,
    debouncedEmployeeSearch,
    rows,
    selectedEmployee?.userId,
  ]);

  useEffect(() => {
    if (!employeePickerOpen) return undefined;
    const onDocMouseDown = (e) => {
      if (employeePickerRef.current && !employeePickerRef.current.contains(e.target)) {
        setEmployeePickerOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [employeePickerOpen]);

  const hasActiveFilters =
    debouncedSearch !== "" ||
    yearFilter !== FILTER.ALL ||
    statusFilter !== FILTER.ALL ||
    departmentFilter !== FILTER.ALL ||
    officeFilter !== FILTER.ALL;

  const clearFilters = () => {
    setSearchQuery("");
    setYearFilter(FILTER.ALL);
    setStatusFilter(FILTER.ALL);
    setDepartmentFilter(FILTER.ALL);
    setOfficeFilter(FILTER.ALL);
  };

  const resetEmployeePicker = () => {
    setSelectedEmployee(null);
    setEmployeeSearchQuery("");
    setDebouncedEmployeeSearch("");
    setEmployeeSuggestions([]);
    setEmployeePickerOpen(false);
    setEmployeeSearchLoading(false);
    setEditScheduleMeta(null);
  };

  const selectEmployee = (emp) => {
    if (!emp.canScheduleIncrement) {
      Common.showErrorMessage(
        emp.hasIncrementOnSelectedDate
          ? t("app.payroll.increments.dateAlreadyTaken")
          : t("app.payroll.increments.employeeCannotSchedule")
      );
      return;
    }
    setSelectedEmployee(emp);
    setForm((prev) => ({
      ...prev,
      userId: emp.userId,
      newGrossSalary:
        emp.defaultGrossSalary != null && Number.isFinite(emp.defaultGrossSalary)
          ? String(emp.defaultGrossSalary)
          : prev.newGrossSalary,
    }));
    setEmployeePickerOpen(false);
    setEmployeeSearchQuery("");
    setDebouncedEmployeeSearch("");
    setEmployeeSuggestions([]);
  };

  const runExportExcel = async () => {
    setExportLoading("excel");
    try {
      const blob = await fetchSalaryIncrementExportExcel(exportQuery);
      triggerBlobDownload(blob, `${salaryIncrementExportBasename()}.xlsx`);
    } catch (e) {
      Common.showErrorMessage(e?.response?.data?.message || e?.message || t("app.payroll.increments.exportFailed"));
    } finally {
      setExportLoading(null);
    }
  };

  const runExportCsv = async () => {
    setExportLoading("csv");
    try {
      const blob = await fetchSalaryIncrementExportCsv(exportQuery);
      triggerBlobDownload(blob, `${salaryIncrementExportBasename()}.csv`);
    } catch (e) {
      Common.showErrorMessage(e?.response?.data?.message || e?.message || t("app.payroll.increments.exportFailed"));
    } finally {
      setExportLoading(null);
    }
  };

  const runExportPdf = async () => {
    setExportLoading("pdf");
    try {
      const blob = await fetchSalaryIncrementExportPdf(exportQuery);
      triggerBlobDownload(blob, `${salaryIncrementExportBasename()}.pdf`);
    } catch (e) {
      Common.showErrorMessage(e?.response?.data?.message || e?.message || t("app.payroll.increments.exportFailed"));
    } finally {
      setExportLoading(null);
    }
  };

  const openCreate = () => {
    setModalMode("create");
    setForm(emptyForm());
    resetEmployeePicker();
    setModalOpen(true);
  };

  const openEdit = (row) => {
    if (row.status === IncrementStatus.Active) {
      Common.showErrorMessage(t("app.payroll.increments.editBlockedActive"));
      return;
    }
    setModalMode("edit");
    resetEmployeePicker();
    setForm({
      id: row.id,
      userId: row.userId,
      newGrossSalary: String(row.newGrossSalary),
      effectiveFrom: toDateInputValue(row.effectiveFrom),
      reason: row.reason,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setForm(emptyForm());
    resetEmployeePicker();
  };

  const reasonLen = form.reason.length;
  const grossNum = parseFloat(String(form.newGrossSalary).trim());
  const effectiveDateIsPast = isEffectiveDateInPast(form.effectiveFrom);
  const canSave =
    (modalMode === "edit" || form.userId) &&
    Number.isFinite(grossNum) &&
    grossNum > 0 &&
    form.effectiveFrom &&
    !effectiveDateIsPast &&
    form.reason.trim() !== "" &&
    reasonLen <= REASON_MAX &&
    !saving &&
    !employeeSearchLoading &&
    (modalMode === "edit"
      ? editScheduleMeta?.canScheduleIncrement === true
      : Boolean(selectedEmployee?.canScheduleIncrement));

  const scheduleHintMeta = modalMode === "edit" ? editScheduleMeta : selectedEmployee;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload =
        modalMode === "create"
          ? {
              userId: form.userId,
              newGrossSalary: grossNum,
              effectiveFrom: form.effectiveFrom,
              reason: form.reason.trim(),
            }
          : {
              id: form.id,
              newGrossSalary: grossNum,
              effectiveFrom: form.effectiveFrom,
              reason: form.reason.trim(),
            };
      const res =
        modalMode === "create" ? await createSalaryIncrement(payload) : await updateSalaryIncrement(payload);
      const { item, message } = unwrapOne(res);
      if (item) {
        Common.showSuccessMessage(
          modalMode === "create"
            ? t("app.payroll.increments.createSuccess")
            : t("app.payroll.increments.updateSuccess")
        );
        closeModal();
        await loadGrid();
      } else {
        Common.showErrorMessage(message || t("app.payroll.increments.saveError"));
      }
    } catch (e) {
      Common.showErrorMessage(e?.response?.data?.message || e?.message || t("app.payroll.increments.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const applyStatusUpdate = async (row, nextStatus) => {
    setStatusUpdatingId(row.id);
    try {
      const res = await updateSalaryIncrementStatus({ id: row.id, status: nextStatus });
      const { item, message } = unwrapOne(res);
      if (item) {
        Common.showSuccessMessage(t("app.payroll.increments.statusUpdateSuccess"));
        await loadGrid();
        return true;
      }
      Common.showErrorMessage(message || t("app.payroll.increments.statusUpdateError"));
      return false;
    } catch (e) {
      Common.showErrorMessage(e?.response?.data?.message || e?.message || t("app.payroll.increments.statusUpdateError"));
      return false;
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleStatusChange = (row, nextStatus) => {
    if (nextStatus === statusApiValue(row.statusLabel)) return;

    if (nextStatus === "Active") {
      if (!isEffectiveDateReached(row.effectiveFrom)) {
        Common.showErrorMessage(t("app.payroll.increments.activateBlockedFuture"));
        return;
      }
      const otherActive = rows.filter(
        (r) => r.userId === row.userId && r.id !== row.id && r.status === IncrementStatus.Active
      );
      setActivateConfirm({ row, nextStatus, otherActiveCount: otherActive.length });
      return;
    }

    applyStatusUpdate(row, nextStatus);
  };

  const closeActivateConfirm = () => {
    if (!activateLoading) setActivateConfirm(null);
  };

  const confirmActivate = async () => {
    if (!activateConfirm) return;
    setActivateLoading(true);
    try {
      const ok = await applyStatusUpdate(activateConfirm.row, activateConfirm.nextStatus);
      if (ok) setActivateConfirm(null);
    } finally {
      setActivateLoading(false);
    }
  };

  const editRowMeta = useMemo(() => {
    if (modalMode !== "edit" || !form.id) return null;
    return rows.find((r) => r.id === form.id) ?? null;
  }, [modalMode, form.id, rows]);

  const modalPreviousGross = useMemo(() => {
    if (modalMode === "edit") {
      if (editScheduleMeta?.previousGrossSalary != null) return editScheduleMeta.previousGrossSalary;
      if (editRowMeta) return editRowMeta.previousGrossSalary;
    }
    if (selectedEmployee?.previousGrossSalary != null) return selectedEmployee.previousGrossSalary;
    return null;
  }, [modalMode, editRowMeta, editScheduleMeta, selectedEmployee]);

  const modalDefaultGross = useMemo(() => {
    if (modalMode !== "create" || !selectedEmployee) return null;
    if (selectedEmployee.defaultGrossSalary != null) return selectedEmployee.defaultGrossSalary;
    return null;
  }, [modalMode, selectedEmployee]);

  const incrementPreview = useMemo(() => {
    if (!Number.isFinite(grossNum) || modalPreviousGross == null || !Number.isFinite(modalPreviousGross)) return null;
    return grossNum - modalPreviousGross;
  }, [grossNum, modalPreviousGross]);

  const columns = useMemo(
    () => [
      { key: "employee", label: t("app.payroll.increments.colEmployee"), sortable: false },
      { key: "prev", label: t("app.payroll.increments.colPrev"), sortable: false },
      { key: "next", label: t("app.payroll.increments.colNew"), sortable: false },
      { key: "increment", label: t("app.payroll.increments.colIncrement"), sortable: false },
      { key: "effective", label: t("app.payroll.increments.colEffective"), sortable: false },
      { key: "status", label: t("app.payroll.increments.colStatus"), sortable: false },
      { key: "source", label: t("app.payroll.increments.colSource", { defaultValue: "Source" }), sortable: false },
      { key: "reason", label: t("app.payroll.increments.colReason"), sortable: false },
      {
        key: "actions",
        label: t("app.payroll.common.actions"),
        sortable: false,
        headerClassName: payrollActionsHeaderClass,
      },
    ],
    [t]
  );

  const showingFrom = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const showingTo = Math.min(page * PAGE_SIZE, totalCount);

  return (
    <div className="documents-page payroll-page ts-page px-3 px-md-4 py-4">
      <CommonPageHeader
        icon={<i className="bi bi-graph-up-arrow" />}
        title={t("app.payroll.increments.title")}
        description={t("app.payroll.increments.subtitle")}
      />

      {loadError && (
        <div className="alert alert-warning d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3" role="alert">
          <span>{loadError}</span>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={loadGrid}>
            {t("app.payroll.common.retry")}
          </button>
        </div>
      )}

      <section className="ts-time-log-shell mb-3" aria-label={t("app.payroll.increments.filtersAria")}>
        <div className="ts-appr-filters" role="search">
          <div className="ts-appr-filters__title text-muted" id="payroll-inc-filters-heading">
            {t("app.payroll.increments.filtersToolbarLabel")}
          </div>

          <div className="ts-appr-filters__toolbar" aria-labelledby="payroll-inc-filters-heading">
            <div className="ts-appr-filters__toolbar-left">
              <div
                className="ts-time-log-export-group ts-time-log-export-group--labeled ts-appr-filters__exports"
                role="group"
                aria-label={t("app.payroll.increments.exportActionsAria")}
              >
                <button
                  type="button"
                  className="ts-tl-export-icon ts-tl-export-icon--excel ts-tl-export-icon--labeled"
                  onClick={runExportExcel}
                  disabled={Boolean(exportLoading) || loading}
                  title={t("app.payroll.increments.exportExcelTitle")}
                >
                  {exportLoading === "excel" ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                  ) : (
                    <i className="bi bi-file-earmark-spreadsheet" aria-hidden />
                  )}
                  <span className="ts-tl-export-label">{t("app.timesheets.common.exportExcelShort")}</span>
                </button>
                <button
                  type="button"
                  className="ts-tl-export-icon ts-tl-export-icon--csv ts-tl-export-icon--labeled"
                  onClick={runExportCsv}
                  disabled={Boolean(exportLoading) || loading}
                  title={t("app.payroll.increments.exportCsvTitle")}
                >
                  {exportLoading === "csv" ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                  ) : (
                    <i className="bi bi-filetype-csv" aria-hidden />
                  )}
                  <span className="ts-tl-export-label">{t("app.timesheets.common.exportCsvShort")}</span>
                </button>
                <button
                  type="button"
                  className="ts-tl-export-icon ts-tl-export-icon--pdf ts-tl-export-icon--labeled"
                  onClick={runExportPdf}
                  disabled={Boolean(exportLoading) || loading}
                  title={t("app.payroll.increments.exportPdfTitle")}
                >
                  {exportLoading === "pdf" ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                  ) : (
                    <i className="bi bi-file-earmark-pdf" aria-hidden />
                  )}
                  <span className="ts-tl-export-label">{t("app.timesheets.common.exportPdfShort")}</span>
                </button>
              </div>
            </div>
          </div>

          <div className="ts-appr-filters__fields payroll-inc-filters__fields">
            <div className="ts-filter-field">
              <label className="ts-tl-filter-bar-label text-muted" htmlFor="payroll-inc-search">
                {t("app.payroll.increments.searchLabel")}
              </label>
              <input
                id="payroll-inc-search"
                type="search"
                autoComplete="off"
                placeholder={t("app.payroll.increments.searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="ts-filter-field">
              <label className="ts-tl-filter-bar-label text-muted" htmlFor="payroll-inc-year">
                {t("app.payroll.increments.filterYear")}
              </label>
              <select
                id="payroll-inc-year"
                value={yearFilter}
                onChange={(e) => {
                  setYearFilter(e.target.value);
                  setPage(1);
                }}
                disabled={loading}
              >
                <option value={FILTER.ALL}>{t("app.payroll.increments.filterAllYears")}</option>
                {yearOptions.map((y) => (
                  <option key={y} value={String(y)}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
            <div className="ts-filter-field">
              <label className="ts-tl-filter-bar-label text-muted" htmlFor="payroll-inc-status">
                {t("app.payroll.increments.filterStatus")}
              </label>
              <select
                id="payroll-inc-status"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                disabled={loading}
              >
                <option value={FILTER.ALL}>{t("app.payroll.increments.filterAllStatuses")}</option>
                <option value="Pending">{t("app.payroll.increments.statusPending")}</option>
                <option value="OnHold">{t("app.payroll.increments.statusOnHold")}</option>
                <option value="Active">{t("app.payroll.increments.statusActive")}</option>
              </select>
            </div>
            <div className="ts-filter-field">
              <label className="ts-tl-filter-bar-label text-muted" htmlFor="payroll-inc-dept">
                {t("app.payroll.increments.filterDepartment")}
              </label>
              <select
                id="payroll-inc-dept"
                value={departmentFilter}
                onChange={(e) => {
                  setDepartmentFilter(e.target.value);
                  setPage(1);
                }}
                disabled={loading}
              >
                <option value={FILTER.ALL}>{t("app.payroll.increments.filterAllDepartments")}</option>
                {departments.map((d) => (
                  <option key={d.id} value={String(d.id)}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="ts-filter-field">
              <label className="ts-tl-filter-bar-label text-muted" htmlFor="payroll-inc-office">
                {t("app.payroll.increments.filterOffice")}
              </label>
              <select
                id="payroll-inc-office"
                value={officeFilter}
                onChange={(e) => {
                  setOfficeFilter(e.target.value);
                  setPage(1);
                }}
                disabled={loading}
              >
                <option value={FILTER.ALL}>{t("app.payroll.increments.filterAllOffices")}</option>
                {offices.map((o) => (
                  <option key={o.id} value={String(o.id)}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="ts-filter-field ts-appr-filters__clear-field">
              <label className="ts-tl-filter-bar-label text-muted" htmlFor="payroll-inc-new">
                {t("app.payroll.increments.actionsLabel")}
              </label>
              <button
                id="payroll-inc-new"
                type="button"
                className="btn btn-primary documents-btn ts-ts-primary-gradient payroll-inc-filters__new-btn"
                onClick={openCreate}
              >
                <i className="bi bi-plus-lg me-1" aria-hidden />
                {t("app.payroll.increments.new")}
              </button>
            </div>
            <div className="ts-filter-field ts-appr-filters__clear-field">
              <label className="ts-tl-filter-bar-label text-muted" htmlFor="payroll-inc-clear">
                {t("app.payroll.increments.clearFiltersLabel")}
              </label>
              <button
                id="payroll-inc-clear"
                type="button"
                className="btn btn-outline-secondary ts-tl-clear-filters-btn ts-appr-filters__clear-btn"
                onClick={clearFilters}
                disabled={loading || !hasActiveFilters}
              >
                <i className="bi bi-x-lg me-1" aria-hidden />
                {t("app.payroll.increments.clearFilters")}
              </button>
            </div>
          </div>
        </div>
      </section>

      <PayrollDataGrid
        columns={columns}
        rows={rows}
        loading={loading}
        loadingMessage={t("app.payroll.increments.loadingGrid")}
        getRowKey={(row) => String(row.id)}
        emptyMessage={t("app.payroll.common.noResults")}
        renderRow={(row) => {
          const canEdit = row.status !== IncrementStatus.Active;
          const canActivate = isEffectiveDateReached(row.effectiveFrom);
          const statusBusy = statusUpdatingId === row.id;
          return (
            <>
              <td>
                <div className="d-flex align-items-center gap-2">
                  {row.imageURL ? (
                    <img
                      src={row.imageURL}
                      alt=""
                      className="rounded-circle flex-shrink-0"
                      width={36}
                      height={36}
                      style={{ objectFit: "cover" }}
                    />
                  ) : (
                    <span
                      className="rounded-circle flex-shrink-0 d-inline-flex align-items-center justify-content-center text-white small fw-semibold"
                      style={{
                        width: 36,
                        height: 36,
                        backgroundColor: avatarColorFromName(row.employeeName),
                        fontSize: "0.75rem",
                      }}
                      aria-hidden
                    >
                      {employeeInitial(row.employeeName)}
                    </span>
                  )}
                  <div className="min-w-0">
                    <div className="fw-semibold text-truncate">{row.employeeName || "—"}</div>
                    {row.designation ? (
                      <div className="small text-muted text-truncate">{row.designation}</div>
                    ) : null}
                    {row.email ? (
                      <div className="small text-muted text-truncate">{row.email}</div>
                    ) : null}
                  </div>
                </div>
              </td>
              <td>{formatIncrementMoney(row.previousGrossSalary)}</td>
              <td>{formatIncrementMoney(row.newGrossSalary)}</td>
              <td className="fw-semibold">{formatIncrementMoney(row.incrementAmount)}</td>
              <td>{formatEffectiveDate(row.effectiveFrom)}</td>
              <td>
                <span className={`payroll-badge ${statusBadgeClass(row.statusLabel)}`}>
                  {row.statusLabel || "—"}
                </span>
              </td>
              <td>
                <span className={`payroll-badge ${sourceBadgeClass(row.source || row.sourceLabel)}`}>
                  {formatSourceLabel(row, t)}
                </span>
              </td>
              <td className="text-break" style={{ maxWidth: 220 }}>
                {row.reason || "—"}
              </td>
              <td className={payrollActionsCellClass}>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={!canEdit || statusBusy}
                    title={!canEdit ? t("app.payroll.increments.editBlockedActive") : undefined}
                    onClick={() => openEdit(row)}
                  >
                    {t("app.payroll.common.edit")}
                  </button>
                  <select
                    key={`${row.id}-${row.statusLabel}-${row.updatedDate}`}
                    className="form-select form-select-sm"
                    style={{ width: "auto", minWidth: "7.5rem" }}
                    value={statusApiValue(row.statusLabel)}
                    disabled={statusBusy}
                    aria-label={t("app.payroll.increments.statusActionAria", { name: row.employeeName })}
                    onChange={(e) => handleStatusChange(row, e.target.value)}
                  >
                    <option value="Pending">{t("app.payroll.increments.statusPending")}</option>
                    <option value="On Hold">{t("app.payroll.increments.statusOnHold")}</option>
                    <option value="Active" disabled={!canActivate}>
                      {t("app.payroll.increments.statusActive")}
                    </option>
                  </select>
                  {statusBusy ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                  ) : null}
                </div>
              </td>
            </>
          );
        }}
      />

      <div className="documents-pagination documents-pagination--grid-pager d-flex justify-content-between align-items-center flex-wrap gap-2 mt-2">
        <span className="documents-pagination-total text-muted small">
          {totalCount === 0
            ? t("app.payroll.common.noResults")
            : t("app.payroll.common.showingRange", { from: showingFrom, to: showingTo, total: totalCount })}
        </span>
        <GridPager
          className="documents-rc-pagination"
          onChange={(p) => setPage(p)}
          current={page}
          total={totalCount}
          pageSize={PAGE_SIZE}
          showLessItems
          showTitle={false}
          showSizeChanger={false}
        />
      </div>

      <PayrollModal
        open={modalOpen}
        onClose={closeModal}
        title={
          modalMode === "create"
            ? t("app.payroll.increments.modalTitle")
            : t("app.payroll.increments.modalEditTitle")
        }
        subtitle={modalMode === "edit" ? editRowMeta?.employeeName : undefined}
        titleId="payroll-increment-modal-title"
        size="large"
        variant="salary"
        headerIcon={<i className="bi bi-graph-up-arrow" aria-hidden />}
        footer={
          <>
            <button type="button" className="att-btn att-btn--ghost" onClick={closeModal} disabled={saving}>
              {t("app.payroll.common.cancel")}
            </button>
            <button
              type="button"
              className="att-btn att-btn--save-primary"
              onClick={handleSave}
              disabled={!canSave}
            >
              {saving ? t("app.payroll.common.saving") : t("app.payroll.increments.submit")}
            </button>
          </>
        }
      >
        <div className="d-flex flex-column gap-3">
          <div>
            <label className="att-modal-field-label" htmlFor="payroll-inc-effective">
              {t("app.payroll.increments.fieldEffective")}
            </label>
            <input
              id="payroll-inc-effective"
              type="date"
              className="form-control"
              min={todayDateInputValue()}
              value={form.effectiveFrom}
              onChange={(e) => setForm((prev) => ({ ...prev, effectiveFrom: e.target.value }))}
            />
            <p className="small text-muted mt-1 mb-0">{t("app.payroll.increments.fieldEffectiveHint")}</p>
            {effectiveDateIsPast ? (
              <p className="small text-danger mt-1 mb-0">{t("app.payroll.increments.effectiveDatePast")}</p>
            ) : null}
            {scheduleHintMeta && !scheduleHintMeta.canScheduleIncrement ? (
              <p className="small text-danger mt-1 mb-0">
                {scheduleHintMeta.hasIncrementOnSelectedDate
                  ? t("app.payroll.increments.dateAlreadyTaken")
                  : t("app.payroll.increments.employeeCannotSchedule")}
                {scheduleHintMeta.nextAvailableEffectiveFrom
                  ? ` ${t("app.payroll.increments.suggestNextDate", {
                      date: formatEffectiveDate(scheduleHintMeta.nextAvailableEffectiveFrom),
                    })}`
                  : null}
              </p>
            ) : null}
          </div>

          {modalMode === "create" ? (
            <div>
              <label className="att-modal-field-label" htmlFor="payroll-inc-emp-search">
                {t("app.payroll.increments.fieldEmployee")}
              </label>
              {!form.effectiveFrom || effectiveDateIsPast ? (
                <p className="small text-muted mb-0">{t("app.payroll.increments.effectiveDateRequiredForEmployee")}</p>
              ) : selectedEmployee ? (
                <div className="payroll-inc-emp-selected">
                  {selectedEmployee.imageURL ? (
                    <img
                      src={selectedEmployee.imageURL}
                      alt=""
                      className="payroll-inc-emp-picker__avatar"
                    />
                  ) : (
                    <span
                      className="payroll-inc-emp-picker__avatar-fallback"
                      style={{ backgroundColor: avatarColorFromName(selectedEmployee.employeeName) }}
                      aria-hidden
                    >
                      {employeeInitial(selectedEmployee.employeeName)}
                    </span>
                  )}
                  <div className="payroll-inc-emp-selected__body">
                    <div className="payroll-inc-emp-selected__name">{selectedEmployee.employeeName}</div>
                    {selectedEmployee.designationName ? (
                      <div className="payroll-inc-emp-selected__sub">{selectedEmployee.designationName}</div>
                    ) : null}
                    <div className="payroll-inc-emp-selected__sub">
                      {[selectedEmployee.departmentName, selectedEmployee.workplaceName].filter(Boolean).join(" · ") ||
                        "—"}
                    </div>
                    {!selectedEmployee.canScheduleIncrement ? (
                      <div className="payroll-inc-emp-selected__sub text-danger">
                        {selectedEmployee.hasIncrementOnSelectedDate
                          ? t("app.payroll.increments.dateAlreadyTaken")
                          : t("app.payroll.increments.employeeCannotSchedule")}
                      </div>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="btn btn-link payroll-inc-emp-selected__change"
                    onClick={() => {
                      resetEmployeePicker();
                      setForm((prev) => ({ ...prev, userId: "", newGrossSalary: "" }));
                    }}
                  >
                    {t("app.payroll.increments.changeEmployee")}
                  </button>
                </div>
              ) : (
                <div className="payroll-inc-emp-picker" ref={employeePickerRef}>
                  <div className="payroll-inc-emp-picker__input-wrap">
                    <input
                      id="payroll-inc-emp-search"
                      type="search"
                      autoComplete="off"
                      className="payroll-inc-emp-picker__input"
                      placeholder={t("app.payroll.increments.employeeSearchPlaceholder")}
                      value={employeeSearchQuery}
                      onChange={(e) => {
                        setEmployeeSearchQuery(e.target.value);
                        setEmployeePickerOpen(true);
                      }}
                      onFocus={() => setEmployeePickerOpen(true)}
                      aria-expanded={employeePickerOpen}
                      aria-controls="payroll-inc-emp-dropdown"
                      aria-autocomplete="list"
                    />
                  </div>
                  {employeePickerOpen ? (
                    <ul id="payroll-inc-emp-dropdown" className="payroll-inc-emp-picker__dropdown" role="listbox">
                      {employeeSearchLoading ? (
                        <li className="payroll-inc-emp-picker__empty">
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
                          {t("app.payroll.increments.employeeSearchLoading")}
                        </li>
                      ) : employeeSuggestions.length === 0 ? (
                        <li className="payroll-inc-emp-picker__empty">
                          {debouncedEmployeeSearch
                            ? t("app.payroll.increments.employeeSearchNoResults")
                            : t("app.payroll.increments.employeeSearchHint")}
                        </li>
                      ) : (
                        employeeSuggestions.map((emp) => {
                          const unavailable = !emp.canScheduleIncrement;
                          return (
                          <li key={emp.userId} role="option" aria-disabled={unavailable}>
                            <button
                              type="button"
                              className={`payroll-inc-emp-picker__option${unavailable ? " payroll-inc-emp-picker__option--disabled" : ""}`}
                              disabled={unavailable}
                              onClick={() => selectEmployee(emp)}
                            >
                              {emp.imageURL ? (
                                <img src={emp.imageURL} alt="" className="payroll-inc-emp-picker__avatar" />
                              ) : (
                                <span
                                  className="payroll-inc-emp-picker__avatar-fallback"
                                  style={{ backgroundColor: avatarColorFromName(emp.employeeName) }}
                                  aria-hidden
                                >
                                  {employeeInitial(emp.employeeName)}
                                </span>
                              )}
                              <div className="payroll-inc-emp-picker__meta">
                                <div className="payroll-inc-emp-picker__name">{emp.employeeName}</div>
                                {emp.designationName ? (
                                  <div className="payroll-inc-emp-picker__line">{emp.designationName}</div>
                                ) : null}
                                <div className="payroll-inc-emp-picker__line">
                                  {[emp.departmentName, emp.workplaceName].filter(Boolean).join(" · ")}
                                </div>
                                {unavailable ? (
                                  <div className="payroll-inc-emp-picker__line text-danger">
                                    {emp.hasIncrementOnSelectedDate
                                      ? t("app.payroll.increments.dateAlreadyTaken")
                                      : t("app.payroll.increments.employeeOptionUnavailable")}
                                  </div>
                                ) : null}
                              </div>
                            </button>
                          </li>
                          );
                        })
                      )}
                    </ul>
                  ) : null}
                </div>
              )}
            </div>
          ) : (
            <div className="payroll-inc-emp-selected">
              {editRowMeta?.imageURL ? (
                <img src={editRowMeta.imageURL} alt="" className="payroll-inc-emp-picker__avatar" />
              ) : (
                <span
                  className="payroll-inc-emp-picker__avatar-fallback"
                  style={{ backgroundColor: avatarColorFromName(editRowMeta?.employeeName) }}
                  aria-hidden
                >
                  {employeeInitial(editRowMeta?.employeeName)}
                </span>
              )}
              <div className="payroll-inc-emp-selected__body">
                <div className="payroll-inc-emp-selected__name">{editRowMeta?.employeeName || "—"}</div>
                {editRowMeta?.designation ? (
                  <div className="payroll-inc-emp-selected__sub">{editRowMeta.designation}</div>
                ) : null}
                {editRowMeta?.email ? (
                  <div className="payroll-inc-emp-selected__sub">{editRowMeta.email}</div>
                ) : null}
              </div>
            </div>
          )}

          {(modalMode === "create" && selectedEmployee) || modalMode === "edit" ? (
            <div>
              <div className="att-modal-field-label mb-2">{t("app.payroll.increments.salarySummaryTitle")}</div>
              <div className="payroll-inc-salary-summary">
                <div className="payroll-inc-salary-summary__item">
                  <span className="payroll-inc-salary-summary__label">
                    {t("app.payroll.increments.fieldPreviousGross")}
                  </span>
                  <span className="payroll-inc-salary-summary__value payroll-inc-salary-summary__value--muted">
                    {formatIncrementMoney(modalPreviousGross)}
                  </span>
                </div>
                {modalMode === "create" && modalDefaultGross != null ? (
                  <div className="payroll-inc-salary-summary__item">
                    <span className="payroll-inc-salary-summary__label">
                      {t("app.payroll.increments.fieldDefaultGross")}
                    </span>
                    <span className="payroll-inc-salary-summary__value payroll-inc-salary-summary__value--muted">
                      {formatIncrementMoney(modalDefaultGross)}
                    </span>
                  </div>
                ) : null}
                <div className="payroll-inc-salary-summary__item payroll-inc-gross-field">
                  <label className="payroll-inc-salary-summary__label" htmlFor="payroll-inc-gross">
                    {t("app.payroll.increments.fieldNewGross")}
                  </label>
                  <input
                    id="payroll-inc-gross"
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-control form-control-sm"
                    value={form.newGrossSalary}
                    onChange={(e) => setForm((prev) => ({ ...prev, newGrossSalary: e.target.value }))}
                  />
                </div>
              </div>
              {incrementPreview != null ? (
                <div className="payroll-inc-salary-summary mt-2" style={{ gridTemplateColumns: "1fr" }}>
                  <div className="payroll-inc-salary-summary__item">
                    <span className="payroll-inc-salary-summary__label">
                      {t("app.payroll.increments.fieldIncrementPreview")}
                    </span>
                    <span
                      className={`payroll-inc-salary-summary__value ${
                        incrementPreview > 0 ? "payroll-inc-salary-summary__value--highlight" : ""
                      }`}
                    >
                      {formatIncrementMoney(incrementPreview)}
                    </span>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          <div>
            <label className="att-modal-field-label" htmlFor="payroll-inc-reason">
              {t("app.payroll.increments.fieldReason")}
            </label>
            <textarea
              id="payroll-inc-reason"
              rows={3}
              maxLength={REASON_MAX}
              className="form-control"
              value={form.reason}
              onChange={(e) => setForm((prev) => ({ ...prev, reason: e.target.value }))}
            />
            <div className="small text-muted text-end mt-1">
              {t("app.payroll.increments.reasonCharCount", { current: reasonLen, max: REASON_MAX })}
            </div>
          </div>
        </div>
      </PayrollModal>

      {activateConfirm ? (
        <div
          className="documents-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="payroll-inc-activate-dialog-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !activateLoading) closeActivateConfirm();
          }}
        >
          <div
            className="documents-modal documents-modal--upload-single documents-modal--delete-confirm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="documents-modal-preview" style={{ borderRight: "none", background: "#fff" }}>
              <div className="documents-upload-modal-header">
                <div>
                  <p className="documents-upload-modal-eyebrow">{t("app.payroll.increments.activateDialogEyebrow")}</p>
                  <h2 className="documents-upload-modal-title" id="payroll-inc-activate-dialog-title">
                    {t("app.payroll.increments.activateConfirmTitle")}
                  </h2>
                </div>
                <button
                  type="button"
                  className="btn btn-light documents-btn"
                  aria-label={t("app.common.close")}
                  disabled={activateLoading}
                  onClick={closeActivateConfirm}
                >
                  {t("app.common.close")}
                </button>
              </div>

              <div className="documents-modal-sheet documents-modal-sheet--upload documents-delete-confirm-body">
                <div className="documents-delete-confirm-summary">
                  <div className="documents-delete-confirm-icon-wrap" aria-hidden>
                    <i className="bi bi-exclamation-triangle" />
                  </div>
                  <p className="documents-delete-confirm-lead">
                    {activateConfirm.otherActiveCount > 0
                      ? t("app.payroll.increments.activateConfirmLeadWithOthers", {
                          count: activateConfirm.otherActiveCount,
                        })
                      : t("app.payroll.increments.activateConfirmLead")}
                  </p>
                </div>

                <div className="documents-delete-confirm-target">
                  <p className="documents-form-label documents-delete-confirm-target-label">
                    {t("app.payroll.increments.activateConfirmTargetLabel")}
                  </p>
                  <p className="documents-delete-confirm-target-name">{activateConfirm.row.employeeName}</p>
                  <p className="small text-muted mb-0">
                    {formatIncrementMoney(activateConfirm.row.previousGrossSalary)} →{" "}
                    {formatIncrementMoney(activateConfirm.row.newGrossSalary)} ·{" "}
                    {formatEffectiveDate(activateConfirm.row.effectiveFrom)}
                  </p>
                </div>

                <div className="documents-modal-actions documents-delete-confirm-actions">
                  <button
                    type="button"
                    className="btn btn-light documents-btn"
                    disabled={activateLoading}
                    onClick={closeActivateConfirm}
                  >
                    {t("app.payroll.common.cancel")}
                  </button>
                  <button
                    type="button"
                    className="btn documents-btn btn-success"
                    disabled={activateLoading}
                    onClick={confirmActivate}
                  >
                    {activateLoading ? t("app.payroll.common.saving") : t("app.payroll.increments.activateConfirmAction")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SalaryIncrementsPage;
